
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Loader2, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const EdgeFunctionDebugger = () => {
  const [agentId, setAgentId] = useState('');
  const [message, setMessage] = useState('Hello, how can you help me?');
  const [isPublic, setIsPublic] = useState(true);
  const [conversationId, setConversationId] = useState('');
  const [functionName, setFunctionName] = useState('chat-with-agent');
  const [requestPayload, setRequestPayload] = useState('{}');
  
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [supabaseUrl, setSupabaseUrl] = useState<string>('');
  const [supabaseKey, setSupabaseKey] = useState<string>('');

  useEffect(() => {
    // Get the Supabase URL and key from env or use default from client
    const url = import.meta.env.VITE_SUPABASE_URL;
    const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!url || !key) {
      throw new Error('Supabase URL or key is not defined. Please check your environment variables.');
    }

    setSupabaseUrl(url);
    setSupabaseKey(key ? '[CONFIGURED]' : '');
    
    addLog(`Supabase URL configured: ${url}`);
    addLog(`Supabase anonymous key: ${key ? '[CONFIGURED]' : '[MISSING]'}`);
  }, []);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toISOString()}] ${message}`]);
  };

  const handleTest = async () => {
    setIsLoading(true);
    setError(null);
    setResponse(null);
    
    try {
      let payload = {};
      try {
        if (functionName === 'chat-with-agent') {
          payload = {
            agent_id: agentId,
            message,
            conversation_id: conversationId || undefined,
            is_public: isPublic,
          };
        } else if (requestPayload) {
          // For other functions, use the custom payload
          payload = JSON.parse(requestPayload);
        }
      } catch (e) {
        throw new Error(`Invalid JSON in payload: ${e.message}`);
      }
      
      addLog(`Testing edge function: ${functionName}`);
      addLog(`Payload: ${JSON.stringify(payload)}`);
      
      const functionUrl = `${supabaseUrl}/functions/v1/${functionName}`;
      addLog(`Calling edge function at: ${functionUrl}`);
      
      const functionResponse = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: isPublic 
            ? `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` 
            : `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify(payload),
      });
      
      addLog(`Response status: ${functionResponse.status} ${functionResponse.statusText}`);
      addLog(`Response headers: ${JSON.stringify(Object.fromEntries(functionResponse.headers))}`);
      
      // Parse response as text first for debugging
      const responseText = await functionResponse.text();
      addLog(`Raw response: ${responseText}`);
      
      // Try to parse the response as JSON
      try {
        // Only try to parse if there's actually content
        const data = responseText.trim() ? JSON.parse(responseText) : {};
        setResponse(data);
        
        if (!functionResponse.ok || data.error) {
          setError(`Error: ${data.error || functionResponse.statusText}`);
          addLog(`Error detected: ${data.error || functionResponse.statusText}`);
        } else {
          addLog(`Success! Response contains ${data.response ? data.response.length : 0} characters`);
        }
      } catch (parseError: any) {
        setError(`Failed to parse response as JSON: ${parseError.message}`);
        addLog(`JSON parse error: ${parseError.message}`);
      }
    } catch (error: any) {
      setError(error.message || "An unknown error occurred");
      addLog(`Exception: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearLogs = () => {
    setLogs([]);
  };

  return (
    <div className="space-y-6">
      {(!supabaseUrl || supabaseUrl.trim() === '') && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Configuration Error</AlertTitle>
          <AlertDescription>
            VITE_SUPABASE_URL environment variable is not defined. Using default from client.
          </AlertDescription>
        </Alert>
      )}
      
      {(!supabaseKey || supabaseKey.trim() === '') && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Configuration Error</AlertTitle>
          <AlertDescription>
            VITE_SUPABASE_ANON_KEY environment variable is not defined. Using default from client.
          </AlertDescription>
        </Alert>
      )}
      
      <Card>
        <CardHeader>
          <CardTitle>Edge Function Debugger</CardTitle>
          <CardDescription>
            Test Supabase edge functions directly
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="function_name">Function Name</Label>
            <Input 
              id="function_name" 
              value={functionName} 
              onChange={(e) => setFunctionName(e.target.value)} 
              placeholder="Enter function name"
            />
          </div>
          
          {functionName === 'chat-with-agent' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="agent_id">Agent ID</Label>
                <Input 
                  id="agent_id" 
                  value={agentId} 
                  onChange={(e) => setAgentId(e.target.value)} 
                  placeholder="Enter agent ID"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea 
                  id="message" 
                  value={message} 
                  onChange={(e) => setMessage(e.target.value)} 
                  placeholder="Enter message" 
                  rows={3}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="conversation_id">Conversation ID (optional)</Label>
                <Input 
                  id="conversation_id" 
                  value={conversationId} 
                  onChange={(e) => setConversationId(e.target.value)} 
                  placeholder="Enter conversation ID (optional)"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="is_public">Public access mode</Label>
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="request_payload">Request Payload (JSON)</Label>
              <Textarea 
                id="request_payload" 
                value={requestPayload} 
                onChange={(e) => setRequestPayload(e.target.value)} 
                placeholder='{"key": "value"}' 
                rows={5}
              />
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button 
            variant="outline" 
            onClick={handleClearLogs}
            disabled={isLoading || logs.length === 0}
          >
            Clear Logs
          </Button>
          <Button 
            onClick={handleTest}
            disabled={isLoading || !functionName || (functionName === 'chat-with-agent' && !agentId)}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Testing...
              </>
            ) : 'Test Function'}
          </Button>
        </CardFooter>
      </Card>

      {(response || error) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              {error ? (
                <>
                  <XCircle className="mr-2 h-5 w-5 text-destructive" />
                  Error
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-5 w-5 text-green-500" />
                  Success
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {error ? (
              <div className="p-4 bg-destructive/10 text-destructive rounded-md">
                {error}
              </div>
            ) : (
              <div className="p-4 bg-muted rounded-md">
                <pre className="whitespace-pre-wrap text-sm">
                  {JSON.stringify(response, null, 2)}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Debug Logs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80 overflow-auto p-4 bg-black text-green-400 font-mono text-xs rounded-md">
            {logs.length === 0 ? (
              <div className="text-gray-500 italic">No logs yet. Run a test to see logs here.</div>
            ) : (
              logs.map((log, index) => (
                <div key={index} className="pb-1">
                  {log}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EdgeFunctionDebugger;
